import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { useAuth } from './context/AuthContext';
import Navigation from './components/Navigation';
import Footer from './components/common/Footer'; // PRD-055 #112: CIG Legal Disclaimer
import { ToastProvider } from './components/Toast';
import PaymentFailureBanner from './components/common/PaymentFailureBanner'; // PRD-066: Global payment failure banner
import CookieConsent from './components/common/CookieConsent'; // GDPR cookie consent banner
import MaintenanceBanner from './components/common/MaintenanceBanner'; // Scheduled maintenance notice
import RealtimeWarning from './components/common/RealtimeWarning'; // WebSocket connectivity warning
import CommodityErrorBoundary from './components/CommodityErrorBoundary';
import ErrorBoundary from './components/ErrorBoundary';
import OfflineBanner from './components/common/OfflineBanner';
import useScrollToFocused from './hooks/useScrollToFocused'; // PRD-082: Virtual keyboard scroll fix

// Eager imports: Core pages needed on first paint (auth, legal, landing, root)
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import TermsPage from './pages/TermsPage';
import TermsModal from './components/TermsModal';
import PrivacyPage from './pages/PrivacyPage';
import CommunityRulesPage from './pages/CommunityRulesPage';
import ComingSoonPage from './pages/ComingSoonPage';
import LandingPage from './pages/LandingPage';
import NotFoundPage from './pages/NotFoundPage';
import AboutPage from './pages/AboutPage';
import FAQPage from './pages/FAQPage';
import GroupSharePage from './pages/GroupSharePage';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';

// PRD-041: Market Dashboard Redesign - Context
import { MarketSelectionProvider } from './context/MarketSelectionContext';

import {
  getEnvironment,
  requiresAuthentication,
  hasEnvironmentAccess,
  getEnvironmentName,
  getAccessDeniedMessage
} from './utils/environmentUtils';
import './App.css';
import './components/EnvironmentProtection.css';
import NebulaBackground from './components/NebulaBackground';
import OrgAuthGuard from './components/OrgAuthGuard';

// Auto-reload on stale chunk errors after deployment
function lazyWithRetry(importFn) {
  return React.lazy(() =>
    importFn().catch((error) => {
      if (error.name === 'ChunkLoadError' && !window.__chunkRetried) {
        window.__chunkRetried = true;
        window.location.reload();
        return new Promise(() => {});
      }
      throw error;
    })
  );
}

// Lazy-loaded pages: Everything else loads on-demand to reduce initial bundle
// webpackChunkName annotations enable named chunks for better caching
const ShareScreenPage = lazyWithRetry(() => import('./pages/ShareScreenPage'));
const LedgerDashboard = lazyWithRetry(() => import('./pages/LedgerDashboard'));
const ChartsPage = lazyWithRetry(() => import(/* webpackChunkName: "charts" */ './pages/ChartsPage'));
const MarketOverviewPage = lazyWithRetry(() => import(/* webpackChunkName: "market" */ './pages/MarketOverviewPage'));
const CommodityDetailPage = lazyWithRetry(() => import(/* webpackChunkName: "market" */ './pages/CommodityDetailPage'));
const SubmitPricePageV2 = lazyWithRetry(() => import('./pages/SubmitPricePageV2'));
const MerchantLocationsPage = lazyWithRetry(() => import(/* webpackChunkName: "locations" */ './pages/MerchantLocationsPage'));
const LocationsPage = lazyWithRetry(() => import(/* webpackChunkName: "locations" */ './pages/LocationsPage'));
const LocationsPageEnhanced = lazyWithRetry(() => import(/* webpackChunkName: "locations" */ './pages/LocationsPageEnhanced'));
const CargoBoardPage = lazyWithRetry(() => import('./pages/CargoBoardPage'));
const GroupShareView = lazyWithRetry(() => import('./pages/GroupShareView'));
const ProfilePage = lazyWithRetry(() => import('./pages/ProfilePage'));
const ProfilePageV2 = lazyWithRetry(() => import('./pages/ProfilePageV2'));
const PublicProfilePage = lazyWithRetry(() => import('./pages/PublicProfilePage'));
const NotificationsPage = lazyWithRetry(() => import('./pages/NotificationsPage'));
const InvitePage = lazyWithRetry(() => import('./pages/InvitePage'));
const InventoryPage = lazyWithRetry(() => import('./pages/InventoryPage'));
const TransactionHistoryPage = lazyWithRetry(() => import('./pages/TransactionHistoryPage'));
const ShipMatrixPage = lazyWithRetry(() => import(/* webpackChunkName: "ships" */ './pages/ShipMatrixPage'));
const WalletDashboard = lazyWithRetry(() => import('./components/wallet').then(m => ({ default: m.WalletDashboard })));
const UserInventoryPage = lazyWithRetry(() => import('./pages/UserInventoryPage'));
const OrgPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgPage'));
const OrgSettingsPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgSettingsPage'));
const OrgInventoryManagementPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgInventoryManagementPage'));
const GearCatalogPage = lazyWithRetry(() => import(/* webpackChunkName: "items" */ './pages/GearCatalogPage'));
const AdminPage = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminPage'));
const AdminDashboard = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminDashboard'));
const AdminDashboardV2 = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminDashboardV2'));
const SubmissionsPage = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/SubmissionsPage'));
const UserManagementPage = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/UserManagementPage'));
const NewsletterManagementPage = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/NewsletterManagementPage'));
const PriceAuditLog = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/PriceAuditLog'));
const LootRatesManagement = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/LootRatesManagement'));
const GearManagerPage = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/GearManagerPage'));
const WikeloContractsPage = lazyWithRetry(() => import('./pages/WikeloContractsPage'));
const ExecutiveHangarInfoPage = lazyWithRetry(() => import('./pages/ExecutiveHangarInfoPage'));
const WikiPage = lazyWithRetry(() => import('./pages/WikiPage'));
const AdminWikiPage = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminWikiPage'));
const AdminDataImport = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminDataImport'));
const EventManagement = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './components/Admin/EventManagement'));
const ActivityTypesManager = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './components/Admin/ActivityTypesManager'));
const ShipMatrixConfigManager = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './components/Admin/ShipMatrixConfigManager'));
const SecurityDashboard = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './components/Admin/SecurityDashboard'));
const ShipAcquisitionManager = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './components/Admin/ShipAcquisitionManager'));
const KeywordModerationPage = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/KeywordModerationPage'));
const MostWantedQueue = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/MostWantedQueue'));
const MFASettings = lazyWithRetry(() => import('./components/MFA').then(m => ({ default: m.MFASettings })));
const SessionManagement = lazyWithRetry(() => import('./components/SessionManagement').then(m => ({ default: m.SessionManagement })));
const AccountDeletion = lazyWithRetry(() => import('./components/AccountDeletion').then(m => ({ default: m.AccountDeletion })));
const PurchasePage = lazyWithRetry(() => import('./pages/PurchasePage'));
const MetricsDashboardPage = lazyWithRetry(() => import('./pages/MetricsDashboardPage'));
const MyHangarPage = lazyWithRetry(() => import(/* webpackChunkName: "ships" */ './pages/MyHangarPage'));
const GoalsPage = lazyWithRetry(() => import('./pages/GoalsPage.jsx'));
const MyLedgerSettingsPage = lazyWithRetry(() => import('./pages/MyLedgerSettingsPage'));
const LeaderboardsPage = lazyWithRetry(() => import('./pages/LeaderboardsPage'));
const MetaLeaderboardsPage = lazyWithRetry(() => import('./pages/MetaLeaderboardsPage'));
const PriceAlertsPage = lazyWithRetry(() => import('./pages/PriceAlertsPage'));
const OrgFundraisingPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgFundraisingPage'));
const OrgMembersPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgMembersPage'));
const OrgCargoSubmitPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgCargoSubmitPage'));
const OrgManifestsPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgManifestsPage'));
const OrgWalletPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgWalletPage'));
const OrgWalletAnalyticsPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgWalletAnalyticsPage'));
const OrgDKPPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgDKPPage'));
const OrgGoalsPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgGoalsPage'));
const ItemDetailPage = lazyWithRetry(() => import(/* webpackChunkName: "items" */ './pages/ItemDetailPage'));
const ArmorMatrixPage = lazyWithRetry(() => import(/* webpackChunkName: "items" */ './pages/ArmorMatrixPage'));
const ItemsDatabaseHub = lazyWithRetry(() => import(/* webpackChunkName: "items" */ './pages/ItemsDatabaseHub'));
const ItemsDatabasePage = lazyWithRetry(() => import(/* webpackChunkName: "items" */ './pages/ItemsDatabasePage'));
const WeaponsPage = lazyWithRetry(() => import(/* webpackChunkName: "items" */ './pages/WeaponsPage'));
const ShipComponentsPage = lazyWithRetry(() => import(/* webpackChunkName: "items" */ './pages/ShipComponentsPage'));
const UnifiedItemsPage = lazyWithRetry(() => import(/* webpackChunkName: "items" */ './pages/UnifiedItemsPage'));
const SubmitNewItemPage = lazyWithRetry(() => import('./pages/SubmitNewItemPage'));
const SubmitNewLocationPage = lazyWithRetry(() => import('./pages/SubmitNewLocationPage'));
const MySubmissionsPage = lazyWithRetry(() => import('./pages/MySubmissionsPage'));
const LocationsDatabasePage = lazyWithRetry(() => import(/* webpackChunkName: "locations" */ './pages/LocationsDatabasePage'));
const LocationDetailPage = lazyWithRetry(() => import(/* webpackChunkName: "locations" */ './pages/LocationDetailPage'));
const LocationSubmissionsReviewPage = lazyWithRetry(() => import('./pages/LocationSubmissionsReviewPage'));
const LootReportReviewPage = lazyWithRetry(() => import('./pages/LootReportReviewPage'));
const MerchantSubmissionsReviewPage = lazyWithRetry(() => import('./pages/MerchantSubmissionsReviewPage'));
const OrgEquipmentPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgEquipmentPage'));
const OrgSubscriptionPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgSubscriptionPage'));
const OrgSeatsPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgSeatsPage'));
const OrgDashboardPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgDashboardPage'));
const OrgCargoKanbanPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgCargoKanbanPage'));
const OrgCargoDetailPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgCargoDetailPage'));
const OrgFleetPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgFleetPage'));
const OrgEventsPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgEventsPage'));
const OrgEventDetailPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgEventDetailPage'));
const OrgOperationsPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgOperationsPage'));
const OrgOperationNewPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgOperationNewPage'));
const OrgOperationDetailPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgOperationDetailPage'));
const PublicOperationPage = lazyWithRetry(() => import('./pages/PublicOperationPage'));
const OrgFinancePage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgFinancePage'));
const OrgAnalyticsPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgAnalyticsPage'));
const OrgRolesPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgRolesPage'));
const OrgInventoryPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgInventoryPage'));
const OrgCraftingBoardPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgCraftingBoardPage')); // PRD-101 Phase 0
const OrgLoadoutTemplatesPage = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgLoadoutTemplatesPage'));
const DirectGoalsPage = lazyWithRetry(() => import('./pages/DirectGoalsPage'));
const ExchangeHubPage = lazyWithRetry(() => import(/* webpackChunkName: "exchange" */ './pages/ExchangeHubPage'));
const MyTransactions = lazyWithRetry(() => import(/* webpackChunkName: "exchange" */ './components/marketplace/MyTransactions'));
const CrewLinkPage = lazyWithRetry(() => import(/* webpackChunkName: "crewlink" */ './pages/CrewLinkPage'));
const BazaarPage = lazyWithRetry(() => import(/* webpackChunkName: "exchange" */ './pages/BazaarPage'));
const SupportPage = lazyWithRetry(() => import('./pages/SupportPage')); // PRD-069
const ItemReviewQueue = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './components/Moderator/ItemReviewQueue'));
const ModeratorPanel = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './components/Moderator/ModeratorPanel'));
const FieldConfigManager = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './components/Admin/FieldConfigManager'));
const ShipHealthDashboard = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './components/Admin/ShipHealthDashboard'));
const PatchManagement = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './components/Admin/PatchManagement'));
const EnvironmentAccessManagement = lazyWithRetry(() => import(/* webpackChunkName: "admin" */ './pages/admin/EnvironmentAccessManagement'));
const VolumeOverlayDemo = lazyWithRetry(() => import(/* webpackChunkName: "charts" */ './components/charts/indicators/VolumeOverlayDemo'));
const RSIOverlayDemo = lazyWithRetry(() => import(/* webpackChunkName: "charts" */ './components/charts/indicators/RSIOverlayDemo'));
const BollingerBandsDemo = lazyWithRetry(() => import(/* webpackChunkName: "charts" */ './components/charts/indicators/BollingerBandsDemo'));
const RouteBuilderPage = lazyWithRetry(() => import(/* webpackChunkName: "routes" */ './pages/RouteBuilderPage'));
const MyRoutesPage = lazyWithRetry(() => import(/* webpackChunkName: "routes" */ './pages/MyRoutesPage'));
const RouteAnalyticsPage = lazyWithRetry(() => import(/* webpackChunkName: "routes" */ './pages/RouteAnalyticsPage'));

// Lazy-load My Ledger pages for code splitting (reduces bundle size)
const MyLedgerDashboard = lazyWithRetry(() => import('./pages/MyLedgerDashboard'));
// DataCollectorPage deprecated — unified into SubmitPricePageV2 (Submit Data)
// WalletSection and GoalsSection deprecated - using WalletV2Page and GoalsPage
const InventorySection = lazyWithRetry(() => import('./pages/InventorySection'));
const UnifiedInventoryPage = lazyWithRetry(() => import('./pages/UnifiedInventoryPage')); // PRD-030
const WishlistPage = lazyWithRetry(() => import('./pages/WishlistPage')); // PRD-030
const WishlistLocationsPage = lazyWithRetry(() => import('./pages/WishlistLocationsPage')); // PRD-030 Phase 3
const LoadoutsTab = lazyWithRetry(() => import('./components/inventory/LoadoutsTab')); // PRD-060 Phase 3
const GearLoadoutsPage = lazyWithRetry(() => import('./pages/GearLoadoutsPage')); // Freemium loadout builder
const ShipPlannerPage = lazyWithRetry(() => import(/* webpackChunkName: "planner" */ './pages/ShipPlannerPage')); // PRD-085 Phase 7a: Ship Loadout Planner
const OrgShowcasePage = lazyWithRetry(() => import('./pages/OrgShowcasePage')); // PRD-085 Phase 5: Org Showcase
const DemoOrgPage = lazyWithRetry(() => import('./pages/DemoOrgPage')); // PRD-090 Phase 2: Demo Org
const MostWantedPage = lazyWithRetry(() => import('./pages/MostWantedPage')); // PRD-086 Phase 7: Most Wanted bounty board
const MiningPage = lazyWithRetry(() => import(/* webpackChunkName: "industry" */ './pages/MiningPage')); // PRD-087: Industry
const RefiningPage = lazyWithRetry(() => import(/* webpackChunkName: "industry" */ './pages/RefiningPage')); // PRD-087: Industry
const MiningRoutePage = lazyWithRetry(() => import(/* webpackChunkName: "industry" */ './pages/MiningRoutePage')); // PRD-087: Route Planner
const CraftingPage = lazyWithRetry(() => import(/* webpackChunkName: "industry" */ './pages/CraftingPage')); // PRD-088: Crafting
const BlueprintDetailPage = lazyWithRetry(() => import(/* webpackChunkName: "crafting" */ './pages/BlueprintDetailPage'));
const MyLedgerMiningPage = lazyWithRetry(() => import('./pages/MyLedgerMiningPage')); // PRD-087: My Ledger Mining
const OrgMiningDashboard = lazyWithRetry(() => import(/* webpackChunkName: "org" */ './pages/OrgMiningDashboard')); // PRD-087: Org Mining
const BudgetManager = lazyWithRetry(() => import('./pages/BudgetManager'));
const ReputationPage = lazyWithRetry(() => import(/* webpackChunkName: "reputation" */ './pages/ReputationPage')); // PRD-096: Reputation Tracking
const MyLedgerBlueprintsPage = lazyWithRetry(() => import(/* webpackChunkName: "blueprints-ledger" */ './pages/MyLedgerBlueprintsPage')); // PRD-097: Blueprint Collection
const MyLedgerCraftingQueuePage = lazyWithRetry(() => import(/* webpackChunkName: "crafting-queue" */ './pages/MyLedgerCraftingQueuePage')); // PRD-097: Crafting Queue
const ShipHangarPage = lazyWithRetry(() => import('./pages/ShipHangarPage'));
const ShipHangarPublicPage = lazyWithRetry(() => import('./pages/ShipHangarPublicPage')); // Freemium ship hangar
const TradeHistoryPage = lazyWithRetry(() => import('./pages/TradeHistoryPage')); // PRD-031 Phase 4
const WalletV2Page = lazyWithRetry(() => import('./pages/WalletV2Page')); // PRD-032 Phase 1

// PRD-047: My-Ledger Layout wrapper
const MyLedgerLayout = lazyWithRetry(() => import('./components/wallet/my-ledger/MyLedgerLayout'));
const DemoLedgerLayout = lazyWithRetry(() => import(/* webpackChunkName: "demo-ledger" */ './components/wallet/my-ledger/DemoLedgerLayout')); // PRD-095: Demo My Ledger

// Export terms version for use in other components
export const TERMS_VERSION = 1;

// PRD-081: Lightweight pageview tracker — fires on route change, no PII
function PageViewTracker() {
  const location = useLocation();
  const { getAccessToken } = useAuth();
  useEffect(() => {
    const sendPageview = async () => {
      const path = location.pathname
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':id')
        .replace(/\/\d+/g, '/:num')
        .split('?')[0];
      const headers = { 'Content-Type': 'application/json' };
      try {
        const token = await getAccessToken?.();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      } catch { /* proceed without auth */ }
      const payload = { page_path: path };
      try {
        const ref = document.referrer;
        if (ref) {
          const refHost = new URL(ref).hostname;
          if (refHost && refHost !== window.location.hostname) {
            payload.referrer = refHost;
          }
        }
      } catch { /* ignore invalid referrer */ }
      fetch(`${process.env.REACT_APP_API_URL || ''}/api/analytics/pageview`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      }).catch(() => {});
    };
    sendPageview(); // Fire-and-forget
  }, [location.pathname, getAccessToken]);
  return null;
}

// Main App component
const AppContent = () => {
  const { currentUser, isLoadingAuth, showTermsModal, acceptLatestTerms } = useAuth();
  useScrollToFocused(); // PRD-082: Auto-scroll focused inputs on mobile virtual keyboard

  // Detect recovery tokens in URL hash and redirect to /reset-password.
  // GoTrue appends #access_token=...&type=recovery to the redirect URL.
  // Without this, the user lands on whichever page GoTrue's Site URL points to (not /reset-password).
  useEffect(() => {
    if (window.location.hash && window.location.pathname !== '/reset-password') {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      if (hashParams.get('type') === 'recovery') {
        window.location.replace('/reset-password' + window.location.hash);
      }
    }
  }, []);

  // Show loading screen while auth is loading
  if (isLoadingAuth) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Check if current path is a legal page that should always be accessible
  const isLegalPage = ['/terms', '/privacy', '/community-rules'].includes(window.location.pathname);

  // CRITICAL: Block users who haven't accepted the latest TOS
  // This renders BEFORE any app content - users cannot access ANYTHING until they accept
  // EXCEPTION: Legal pages must be accessible so users can read them before accepting
  if (currentUser && showTermsModal && !isLegalPage) {
    return (
      <div className="App">
        <NebulaBackground />
        <TermsModal onAccept={acceptLatestTerms} />
      </div>
    );
  }

  // IMMEDIATE environment protection check
  const environment = getEnvironment();
  const needsAuth = requiresAuthentication();
  const hasAccess = hasEnvironmentAccess(currentUser);

  // Show coming soon only on production domains (not preview URLs)
  const isPreviewUrl = window.location.hostname.includes('vercel.app') &&
                        !window.location.hostname.startsWith('theorbitalledger');
  const showComingSoon = process.env.REACT_APP_SHOW_COMING_SOON === 'true' && !isPreviewUrl;

  if (showComingSoon) {
    return (
      <div className="App">
        <NebulaBackground />
        <ComingSoonPage />
      </div>
    );
  }

  // Access Denied Component - for authenticated users without proper access
  const AccessDeniedPage = () => {
    const envName = getEnvironmentName();
    const message = getAccessDeniedMessage();

    return (
      <div className="environment-access-denied">
        <div className="access-denied-container">
          <div className="access-denied-icon">🔒</div>
          <h1>Access Denied</h1>
          <div className="environment-badge">{envName} Environment</div>
          <p className="access-denied-message">{message}</p>

          {currentUser && (
            <div className="user-info">
              <p><strong>Current User:</strong> {currentUser.username || currentUser.email}</p>
              <p><strong>Role:</strong> {currentUser.role || 'user'}</p>
              {environment === 'dev' && (
                <p><strong>Dev Access:</strong> {currentUser.dev_access ? '✓ Enabled' : '✗ Not Enabled'}</p>
              )}
              {environment === 'beta' && (
                <p><strong>Beta Access:</strong> {currentUser.beta_access ? '✓ Enabled' : '✗ Not Enabled'}</p>
              )}
            </div>
          )}

          <div className="access-denied-actions">
            <a href="mailto:support@theorbitalledger.com" className="btn-contact">
              Contact Support
            </a>
            <a href="https://theorbitalledger.com" className="btn-production">
              Go to Production Site
            </a>
          </div>
        </div>
      </div>
    );
  };



  return (
    <Router>
      <PageViewTracker />
      <ToastProvider>
        <div className="App">
          {/* Starfield background behind all content */}
          <NebulaBackground />
          {/* Maintenance banner - shown above navigation when REACT_APP_MAINTENANCE_MESSAGE is set */}
          <MaintenanceBanner />
          {/* Only show navigation if user has access or environment doesn't require auth */}
          {(!needsAuth || hasAccess) && <Navigation />}
          {/* PRD-066: Global payment failure banner - shown after nav, before content */}
          {(!needsAuth || hasAccess) && <PaymentFailureBanner />}
          {/* Realtime connectivity warning - shown when WebSocket blocked by ad blocker/privacy extension */}
          {(!needsAuth || hasAccess) && <RealtimeWarning />}
          <OfflineBanner />
          <main id="main-content" className="main-content">
            <ErrorBoundary>
            <Suspense fallback={<div className="loading-screen"><div className="loading-spinner"></div><p>Loading...</p></div>}>
            <Routes>
            {/* Public demo routes - Always accessible, no authentication required */}
            <Route path="/demo/volume-overlay" element={<VolumeOverlayDemo />} />
            <Route path="/demo/rsi-overlay" element={<RSIOverlayDemo />} />
            <Route path="/demo/bollinger-bands" element={<BollingerBandsDemo />} />

            {/* Route Planner - FREE, No authentication required */}
            <Route path="/route-planner" element={<RouteBuilderPage />} />
            <Route path="/tools/route-builder" element={<RouteBuilderPage />} />

            {/* My Routes - Protected route for saved routes */}
            <Route path="/my-routes" element={currentUser ? <MyRoutesPage /> : <Navigate to="/login" />} />

            {/* Route Analytics - Protected route for route performance tracking */}
            <Route path="/route-analytics" element={currentUser ? <RouteAnalyticsPage /> : <Navigate to="/login" />} />

            {/* Environment-protected routing */}
            {needsAuth && !currentUser && (
              <>
                {/* Only allow login/register routes when auth is required but user not logged in */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                {/* PRD-037 Task 2.2: Password reset routes */}
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                {/* Legal and informational pages must be accessible without login */}
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/community-rules" element={<CommunityRulesPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/support" element={<SupportPage />} />
                {/* PRD-095: Demo My Ledger — accessible without login */}
                <Route path="/demo/my-ledger" element={<Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Demo...</div>}><DemoLedgerLayout><MyLedgerDashboard /></DemoLedgerLayout></Suspense>} />
                <Route path="/demo/my-ledger/*" element={<Suspense fallback={null}><DemoLedgerLayout><MyLedgerDashboard /></DemoLedgerLayout></Suspense>} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </>
            )}
            {needsAuth && currentUser && !hasAccess && (
              <>
                {/* Legal pages must be accessible even without environment access */}
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/community-rules" element={<CommunityRulesPage />} />
                {/* PRD-095: Demo My Ledger — accessible without environment access */}
                <Route path="/demo/my-ledger" element={<Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Demo...</div>}><DemoLedgerLayout><MyLedgerDashboard /></DemoLedgerLayout></Suspense>} />
                <Route path="/demo/my-ledger/*" element={<Suspense fallback={null}><DemoLedgerLayout><MyLedgerDashboard /></DemoLedgerLayout></Suspense>} />
                {/* Show access denied for authenticated users without proper access */}
                <Route path="*" element={<AccessDeniedPage />} />
              </>
            )}
            {(!needsAuth || hasAccess) && (
              <>
            {/* Public routes - No authentication required */}
            <Route path="/login" element={!currentUser ? <LoginPage /> : <Navigate to="/" />} />
            <Route path="/register" element={!currentUser ? <RegisterPage /> : <Navigate to="/" />} />
            {/* PRD-037 Task 2.2: Password reset routes */}
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/community-rules" element={<CommunityRulesPage />} />
            <Route path="/invite/:code" element={<InvitePage />} />
            <Route path="/join/:code" element={<InvitePage />} />
            <Route path="/purchase" element={<PurchasePage />} />
            <Route path="/benefits" element={<PurchasePage />} />

            {/* Public data routes - No authentication required */}
            <Route path="/" element={currentUser ? <GroupSharePage /> : <LandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/groupshare/view/:shareId" element={<GroupShareView />} />
            <Route path="/groupshare/view" element={<GroupShareView />} />
            <Route path="/op/:shareToken" element={<PublicOperationPage />} />
            <Route path="/ledger-dashboard" element={<LedgerDashboard />} />
            {/* PRD-041: Market Dashboard Routes - Wrapped in MarketSelectionProvider */}
            <Route path="/market-overview" element={
              <MarketSelectionProvider>
                <MarketOverviewPage />
              </MarketSelectionProvider>
            } />
            <Route path="/commodities" element={<Navigate to="/market-overview" replace />} />
            <Route path="/commodities/:slug" element={<CommodityErrorBoundary><CommodityDetailPage /></CommodityErrorBoundary>} />
            <Route path="/commodities/:slug/:tab" element={<CommodityErrorBoundary><CommodityDetailPage /></CommodityErrorBoundary>} />
            <Route path="/charts" element={
              <MarketSelectionProvider>
                <ChartsPage />
              </MarketSelectionProvider>
            } />
            <Route path="/locations" element={<LocationsPageEnhanced />} />
            <Route path="/locations-old" element={<LocationsPage />} />
            <Route path="/merchant-locations" element={<MerchantLocationsPage />} />
            <Route path="/merchant-locations/:merchantId" element={<MerchantLocationsPage />} />
            {/* Redirect legacy Ships page to Ship Matrix */}
            <Route path="/ships" element={<Navigate to="/ship-matrix" replace />} />
            <Route path="/ship-matrix" element={<ShipMatrixPage />} />
            <Route path="/gear" element={<GearCatalogPage />} />
            <Route path="/info/wikelo" element={<WikeloContractsPage />} />
            <Route path="/info/executive-hangar" element={<ExecutiveHangarInfoPage />} /> {/* PRD-051 */}
            <Route path="/wiki/:slug" element={<WikiPage />} />
            {/* Unified Items Database - New */}
            <Route path="/items" element={<UnifiedItemsPage />} />
            <Route path="/items/submit" element={currentUser ? <SubmitNewItemPage /> : <Navigate to="/login" />} />
            <Route path="/items/my-submissions" element={currentUser ? <MySubmissionsPage /> : <Navigate to="/login" />} />
            {/* SEO-friendly item detail route (slug-based) */}
            <Route path="/items/:slug" element={<ItemDetailPage />} />
            {/* Location Database - PRD-022A */}
            <Route path="/database/locations" element={<LocationsDatabasePage />} />
            <Route path="/database/locations/submit" element={currentUser ? <SubmitNewLocationPage /> : <Navigate to="/login" />} />
            <Route path="/database/locations/:slug" element={<LocationDetailPage />} />
            <Route path="/database/locations/edit/:slug" element={currentUser ? <SubmitNewLocationPage /> : <Navigate to="/login" />} />
            {/* Location Submission - PRD-020 */}
            <Route path="/locations/submit" element={currentUser ? <SubmitNewLocationPage /> : <Navigate to="/login" />} />
            {/* Legacy Item Routes - Keep for backward compatibility */}
            <Route path="/items-database" element={<ItemsDatabaseHub />} />
            <Route path="/info/items" element={<ItemsDatabasePage />} />
            <Route path="/info/items/:id" element={<ItemDetailPage />} />
            <Route path="/armor-matrix" element={<ArmorMatrixPage />} />
            <Route path="/armor" element={<ArmorMatrixPage />} />
            <Route path="/weapons" element={<WeaponsPage />} />
            <Route path="/ship-components" element={<Navigate to="/ship-planner" replace />} />
            {/* PRD-086 Phase 7: Most Wanted bounty board */}
            <Route path="/most-wanted" element={<MostWantedPage />} />
            {/* PRD-085 Phase 5: Org Showcase marketing page */}
            <Route path="/features/orgs" element={<OrgShowcasePage />} />
            <Route path="/goals-direct" element={<DirectGoalsPage />} />

            {/* The Exchange - Marketplace & LFG */}
            <Route path="/exchange" element={<ExchangeHubPage />} />
            <Route path="/marketplace" element={<Navigate to="/exchange" replace />} />
            <Route path="/my-transactions" element={currentUser ? <MyTransactions /> : <Navigate to="/login" />} />

            {/* PRD-087: Industry */}
            <Route path="/industry/mining" element={<MiningPage />} />
            <Route path="/industry/refining" element={<RefiningPage />} />
            <Route path="/industry/mining/route" element={<MiningRoutePage />} />
            <Route path="/crafting/:slug" element={<BlueprintDetailPage />} />
            <Route path="/industry/crafting" element={<CraftingPage />} />

            {/* PRD-020: CrewLink, Bazaar, and Player Economy */}
            <Route path="/crewlink" element={<CrewLinkPage />} />
            <Route path="/lfg" element={<Navigate to="/crewlink" replace />} /> {/* Redirect old LFG to CrewLink */}
            <Route path="/bazaar" element={<BazaarPage />} />

            {/* Protected routes - Require authentication */}
            <Route path="/submit-data" element={currentUser ? <SubmitPricePageV2 /> : <Navigate to="/login" />} />
            {/* Legacy redirects → unified Submit Data page */}
            <Route path="/submit-price" element={<Navigate to="/submit-data" replace />} />
            <Route path="/submit-price/screenshot" element={<Navigate to="/submit-data" replace />} />
            <Route path="/submit-price/data-collector" element={<Navigate to="/submit-data" replace />} />
            <Route path="/share" element={currentUser ? <ShareScreenPage /> : <Navigate to="/login" />} />

            {/* PRD-065: Profile V2 Dashboard */}
            <Route path="/profile" element={currentUser ? <ProfilePageV2 /> : <Navigate to="/login" />} />
            {/* PRD-065: Old profile page kept as rollback */}
            <Route path="/profile-legacy" element={currentUser ? <ProfilePage /> : <Navigate to="/login" />} />
            <Route path="/notifications" element={currentUser ? <NotificationsPage /> : <Navigate to="/login" />} />
            {/* PRD-037 Phase 6: MFA Settings */}
            <Route path="/settings/security" element={currentUser ? <MFASettings /> : <Navigate to="/login" />} />
            {/* PRD-037 Phase 6: Session Management */}
            <Route path="/settings/sessions" element={currentUser ? <SessionManagement /> : <Navigate to="/login" />} />
            {/* PRD-037 Phase 6: Account Management (GDPR) */}
            <Route path="/settings/account" element={currentUser ? <AccountDeletion /> : <Navigate to="/login" />} />
            {/* Public Profile - PRD-028 Phase 4: viewable by anyone */}
            <Route path="/profile/:identifier" element={<PublicProfilePage />} />

            {/* Paid features - Require authentication (will be built out later) */}
            <Route path="/inventory" element={currentUser ? <InventoryPage /> : <Navigate to="/login" />} />
            <Route path="/metrics" element={currentUser ? <MetricsDashboardPage /> : <Navigate to="/login" />} />
            <Route path="/my-hangar" element={currentUser ? <MyHangarPage /> : <Navigate to="/login" />} />
            {/* Ship Hangar redirects to Ship Planner (unified freemium demo) */}
            <Route path="/ship-hangar" element={<Navigate to="/ship-planner" replace />} />
            <Route path="/goals" element={currentUser ? <GoalsPage /> : <Navigate to="/login" />} />
            <Route path="/transaction-history" element={currentUser ? <TransactionHistoryPage /> : <Navigate to="/login" />} />
            <Route path="/wallet" element={currentUser ? <WalletDashboard /> : <Navigate to="/login" />} />

            {/* My Ledger Premium Routes - PRD-047: Wrapped with MyLedgerLayout for sidebar navigation */}
            <Route path="/my-ledger" element={
              currentUser ? (
                <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading My Ledger...</div>}>
                  <MyLedgerLayout><MyLedgerDashboard /></MyLedgerLayout>
                </Suspense>
              ) : <Navigate to="/login" />
            } />
            {/* PRD-032: Unified Wallet - consolidated from separate Wallet and Transactions pages */}
            <Route path="/my-ledger/wallet" element={
              currentUser ? (
                <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Wallet...</div>}>
                  <MyLedgerLayout><WalletV2Page /></MyLedgerLayout>
                </Suspense>
              ) : <Navigate to="/login" />
            } />
            {/* Redirect old transactions route to wallet */}
            <Route path="/my-ledger/transactions" element={<Navigate to="/my-ledger/wallet" replace />} />
            {/* Goals: ship purchase, Wikelo contracts, item collection */}
            <Route path="/my-ledger/goals" element={
              currentUser ? (
                <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Goals...</div>}>
                  <MyLedgerLayout><GoalsPage /></MyLedgerLayout>
                </Suspense>
              ) : <Navigate to="/login" />
            } />
            {/* PRD-087: My Ledger Mining & Refining */}
            <Route path="/my-ledger/mining" element={
              currentUser ? (
                <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Mining...</div>}>
                  <MyLedgerLayout><MyLedgerMiningPage /></MyLedgerLayout>
                </Suspense>
              ) : <Navigate to="/login" />
            } />
            {/* PRD-096: Faction Reputation Tracking */}
            <Route path="/my-ledger/reputation" element={
              currentUser ? (
                <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Reputation...</div>}>
                  <MyLedgerLayout><ReputationPage /></MyLedgerLayout>
                </Suspense>
              ) : <Navigate to="/login" />
            } />
            {/* PRD-030: Personal Inventory Management System */}
            <Route path="/my-ledger/inventory" element={
              currentUser ? (
                <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Personal Inventory...</div>}>
                  <MyLedgerLayout><UnifiedInventoryPage /></MyLedgerLayout>
                </Suspense>
              ) : <Navigate to="/login" />
            } />
            {/* Legacy inventory endpoint kept for backward compatibility */}
            <Route path="/my-ledger/inventory-legacy" element={
              currentUser ? (
                <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Inventory...</div>}>
                  <MyLedgerLayout><InventorySection /></MyLedgerLayout>
                </Suspense>
              ) : <Navigate to="/login" />
            } />
            {/* Public Gear Loadouts (freemium — free users get 1 loadout with 24h TTL) */}
            <Route path="/gear-loadouts" element={
              <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Gear Loadouts...</div>}>
                <GearLoadoutsPage />
              </Suspense>
            } />
            {/* PRD-085 Phase 7: /gear-planner alias for /gear-loadouts */}
            <Route path="/gear-planner" element={<Navigate to="/gear-loadouts" replace />} />
            {/* PRD-085 Phase 7a: Public Ship Loadout Planner (free to use, save behind premium) */}
            <Route path="/ship-planner" element={
              <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Ship Planner...</div>}>
                <ShipPlannerPage />
              </Suspense>
            } />
            {/* PRD-060 Phase 3: Gear Loadouts (premium, inside My Ledger) */}
            <Route path="/my-ledger/loadouts" element={
              currentUser ? (
                <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Loadouts...</div>}>
                  <MyLedgerLayout><LoadoutsTab /></MyLedgerLayout>
                </Suspense>
              ) : <Navigate to="/login" />
            } />
            {/* PRD-030: Wishlist pages */}
            <Route path="/my-ledger/wishlist" element={
              currentUser ? (
                <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Wishlist...</div>}>
                  <MyLedgerLayout><WishlistPage /></MyLedgerLayout>
                </Suspense>
              ) : <Navigate to="/login" />
            } />
            {/* PRD-030 Phase 3: Wishlist grouped by location */}
            <Route path="/my-ledger/wishlist/by-location" element={
              currentUser ? (
                <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Shopping List...</div>}>
                  <MyLedgerLayout><WishlistLocationsPage /></MyLedgerLayout>
                </Suspense>
              ) : <Navigate to="/login" />
            } />
            <Route path="/my-ledger/ships" element={
              currentUser ? (
                <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Ship Hangar...</div>}>
                  <MyLedgerLayout><ShipHangarPage /></MyLedgerLayout>
                </Suspense>
              ) : <Navigate to="/login" />
            } />
            {/* PRD-097: Blueprint Collection */}
            <Route path="/my-ledger/blueprints" element={
              currentUser ? (
                <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Blueprints...</div>}>
                  <MyLedgerLayout><MyLedgerBlueprintsPage /></MyLedgerLayout>
                </Suspense>
              ) : <Navigate to="/login" />
            } />
            {/* PRD-097: Crafting Queue */}
            <Route path="/my-ledger/crafting-queue" element={
              currentUser ? (
                <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Crafting Queue...</div>}>
                  <MyLedgerLayout><MyLedgerCraftingQueuePage /></MyLedgerLayout>
                </Suspense>
              ) : <Navigate to="/login" />
            } />
            <Route path="/my-ledger/budget" element={
              currentUser ? (
                <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Budget Manager...</div>}>
                  <MyLedgerLayout><BudgetManager /></MyLedgerLayout>
                </Suspense>
              ) : <Navigate to="/login" />
            } />
            {/* My Ledger Settings - PRD-028 Phase 4: Privacy & Profile Settings */}
            <Route path="/my-ledger/settings" element={
              currentUser ? (
                <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Settings...</div>}>
                  <MyLedgerLayout><MyLedgerSettingsPage /></MyLedgerLayout>
                </Suspense>
              ) : <Navigate to="/login" />
            } />
            {/* API Keys temporarily disabled — bandwidth/security review pending
            <Route path="/my-ledger/api" element={
              currentUser ? (
                <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading API Keys...</div>}>
                  <MyLedgerLayout><ApiKeysPage /></MyLedgerLayout>
                </Suspense>
              ) : <Navigate to="/login" />
            } /> */}
            {/* PRD-031 Phase 4: Trade History & Analytics */}
            <Route path="/my-ledger/trade-history" element={
              currentUser ? (
                <Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Trade History...</div>}>
                  <MyLedgerLayout><TradeHistoryPage /></MyLedgerLayout>
                </Suspense>
              ) : <Navigate to="/login" />
            } />
            {/* PRD-064: Redirect old badges route to profile */}
            <Route path="/my-ledger/badges" element={<Navigate to="/profile" replace />} />
            {/* Leaderboards - PRD-028 Phase 4: Public leaderboards */}
            <Route path="/leaderboards" element={<LeaderboardsPage />} />
            {/* Meta Leaderboards - PRD-060 Phase 4: Item Voting Rankings */}
            <Route path="/meta-leaderboards" element={<MetaLeaderboardsPage />} />
            {/* Price Alerts - PRD-055 #119: Price Alert System */}
            <Route path="/price-alerts" element={currentUser ? <PriceAlertsPage /> : <Navigate to="/login" />} />

            <Route path="/group-share/:transactionId" element={currentUser ? <GroupSharePage /> : <Navigate to="/login" />} />
            <Route path="/user-inventory" element={currentUser ? <UserInventoryPage /> : <Navigate to="/login" />} />
            {/* Org routes must include orgId */}
            <Route path="/org-inventory-management" element={currentUser ? <OrgInventoryManagementPage /> : <Navigate to="/login" />} />
            {/* PRD-090 Phase 2: Demo org — redirect /org/demo to real demo org ID */}
            <Route path="/org/demo" element={<Navigate to="/org/demo-stellar-vanguard" replace />} />
            <Route path="/org/demo/*" element={<Navigate to="/org/demo-stellar-vanguard" replace />} />

            {/* PRD-095: Demo My Ledger — public, no auth required */}
            <Route path="/demo/my-ledger" element={<Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>Loading Demo...</div>}><DemoLedgerLayout><MyLedgerDashboard /></DemoLedgerLayout></Suspense>} />
            <Route path="/demo/my-ledger/wallet" element={<Suspense fallback={null}><DemoLedgerLayout><WalletV2Page /></DemoLedgerLayout></Suspense>} />
            <Route path="/demo/my-ledger/inventory" element={<Suspense fallback={null}><DemoLedgerLayout><UnifiedInventoryPage /></DemoLedgerLayout></Suspense>} />
            <Route path="/demo/my-ledger/mining" element={<Suspense fallback={null}><DemoLedgerLayout><MyLedgerMiningPage /></DemoLedgerLayout></Suspense>} />
            <Route path="/demo/my-ledger/blueprints" element={<Suspense fallback={null}><DemoLedgerLayout><MyLedgerBlueprintsPage /></DemoLedgerLayout></Suspense>} />
            <Route path="/demo/my-ledger/crafting-queue" element={<Suspense fallback={null}><DemoLedgerLayout><MyLedgerCraftingQueuePage /></DemoLedgerLayout></Suspense>} />
            <Route path="/demo/my-ledger/reputation" element={<Suspense fallback={null}><DemoLedgerLayout><ReputationPage /></DemoLedgerLayout></Suspense>} />
            <Route path="/demo/my-ledger/loadouts" element={<Suspense fallback={null}><DemoLedgerLayout><LoadoutsTab /></DemoLedgerLayout></Suspense>} />
            <Route path="/demo/my-ledger/wishlist" element={<Suspense fallback={null}><DemoLedgerLayout><WishlistPage /></DemoLedgerLayout></Suspense>} />
            <Route path="/demo/my-ledger/goals" element={<Suspense fallback={null}><DemoLedgerLayout><GoalsPage /></DemoLedgerLayout></Suspense>} />
            <Route path="/demo/my-ledger/ships" element={<Suspense fallback={null}><DemoLedgerLayout><ShipHangarPage /></DemoLedgerLayout></Suspense>} />
            <Route path="/demo/my-ledger/budget" element={<Suspense fallback={null}><DemoLedgerLayout><BudgetManager /></DemoLedgerLayout></Suspense>} />

            {/* PRD-033: Main org route now loads new Org Hub Dashboard */}
            <Route path="/org/:orgId" element={<OrgAuthGuard><OrgDashboardPage /></OrgAuthGuard>} />
            {/* Legacy org dashboard available at /org/:orgId/legacy */}
            <Route path="/org/:orgId/legacy" element={<OrgAuthGuard><OrgPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/cargo/submit" element={<OrgAuthGuard><OrgCargoSubmitPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/cargo/manifests" element={<OrgAuthGuard><OrgManifestsPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/wallet" element={<OrgAuthGuard><OrgWalletPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/wallet/analytics" element={<OrgAuthGuard><OrgWalletAnalyticsPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/dkp" element={<OrgAuthGuard><OrgDKPPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/goals" element={<OrgAuthGuard><OrgGoalsPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/settings" element={<OrgAuthGuard><OrgSettingsPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/cargo-board" element={<OrgAuthGuard><CargoBoardPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/equipment" element={<OrgAuthGuard><OrgEquipmentPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/fundraising" element={<OrgAuthGuard><OrgFundraisingPage /></OrgAuthGuard>} />
            {/* PRD-033 Phase 1: Org Hub subscription and seat management */}
            <Route path="/org/:orgId/subscription" element={<OrgAuthGuard><OrgSubscriptionPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/seats" element={<OrgAuthGuard><OrgSeatsPage /></OrgAuthGuard>} />
            {/* /org/:orgId/hub redirects to main org route for backwards compatibility */}
            <Route path="/org/:orgId/hub" element={<Navigate to={`/org/${window.location.pathname.split('/')[2]}`} replace />} />
            {/* PRD-033 Phase 2: Cargo Operations and Fleet Management */}
            <Route path="/org/:orgId/cargo/:cargoId" element={<OrgAuthGuard><OrgCargoDetailPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/cargo" element={<OrgAuthGuard><OrgCargoKanbanPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/fleet" element={<OrgAuthGuard><OrgFleetPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/inventory" element={<OrgAuthGuard><OrgInventoryPage /></OrgAuthGuard>} />
            {/* PRD-101 Phase 0: Org Crafting Board */}
            <Route path="/org/:orgId/crafting" element={<OrgAuthGuard><OrgCraftingBoardPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/mining" element={<OrgAuthGuard><OrgMiningDashboard /></OrgAuthGuard>} />
            {/* PRD-033 Phase 3: Events and Planning */}
            <Route path="/org/:orgId/events/:eventId" element={<OrgAuthGuard><OrgEventDetailPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/events" element={<OrgAuthGuard><OrgEventsPage /></OrgAuthGuard>} />
            {/* PRD-039 Phase 3: Operation Plans */}
            <Route path="/org/:orgId/operations/new" element={<OrgAuthGuard><OrgOperationNewPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/operations/:planId" element={<OrgAuthGuard><OrgOperationDetailPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/operations" element={<OrgAuthGuard><OrgOperationsPage /></OrgAuthGuard>} />
            {/* PRD-033 Phase 4: Finance & Rewards */}
            <Route path="/org/:orgId/finance" element={<OrgAuthGuard><OrgFinancePage /></OrgAuthGuard>} />
            {/* PRD-033 Phase 5: Analytics */}
            <Route path="/org/:orgId/analytics" element={<OrgAuthGuard><OrgAnalyticsPage /></OrgAuthGuard>} />
            {/* PRD-033 Phase 1: Member Management */}
            <Route path="/org/:orgId/members" element={<OrgAuthGuard><OrgMembersPage /></OrgAuthGuard>} />
            <Route path="/org/:orgId/roles" element={<OrgAuthGuard><OrgRolesPage /></OrgAuthGuard>} />
            {/* PRD-046 Phase 5: Loadout Templates */}
            <Route path="/org/:orgId/loadout-templates" element={<OrgAuthGuard><OrgLoadoutTemplatesPage /></OrgAuthGuard>} />

            {/* Moderator routes */}
            <Route path="/moderator/review-queue" element={['moderator', 'admin', 'owner'].includes(currentUser?.role) ? <ItemReviewQueue /> : <Navigate to="/" />} />
            <Route path="/moderator/reports" element={['moderator', 'admin', 'owner'].includes(currentUser?.role) ? <ModeratorPanel /> : <Navigate to="/" />} />
            <Route path="/moderator/location-submissions" element={['moderator', 'admin', 'owner'].includes(currentUser?.role) ? <LocationSubmissionsReviewPage /> : <Navigate to="/" />} />
            <Route path="/moderator/merchant-submissions" element={['moderator', 'admin', 'owner'].includes(currentUser?.role) ? <MerchantSubmissionsReviewPage /> : <Navigate to="/" />} />
            <Route path="/moderator/loot-reports" element={['moderator', 'admin', 'owner'].includes(currentUser?.role) ? <LootReportReviewPage /> : <Navigate to="/" />} />

            {/* Admin routes */}
            <Route path="/admin" element={currentUser?.role === 'admin' || currentUser?.role === 'owner' ? <AdminPage /> : <Navigate to="/" />} />
            <Route path="/admin/dashboard" element={currentUser?.role === 'admin' || currentUser?.role === 'owner' ? <AdminDashboard /> : <Navigate to="/" />} />
            {/* Admin Dashboard V2 - Equipment Database Management */}
            <Route path="/admin/v2" element={<ProtectedAdminRoute><AdminDashboardV2 /></ProtectedAdminRoute>} />
            <Route path="/admin/submissions" element={currentUser?.role === 'admin' || currentUser?.role === 'owner' ? <SubmissionsPage /> : <Navigate to="/" />} />
            <Route path="/admin/users" element={currentUser?.role === 'admin' || currentUser?.role === 'owner' ? <UserManagementPage /> : <Navigate to="/" />} />
            <Route path="/admin/newsletter" element={currentUser?.role === 'admin' || currentUser?.role === 'owner' ? <NewsletterManagementPage /> : <Navigate to="/" />} />
            <Route path="/admin/price-audit" element={currentUser?.role === 'admin' || currentUser?.role === 'owner' ? <PriceAuditLog /> : <Navigate to="/" />} />
            <Route path="/admin/loot-rates" element={['admin', 'owner', 'moderator'].includes(currentUser?.role) ? <LootRatesManagement /> : <Navigate to="/" />} />
            <Route path="/admin/wiki" element={currentUser?.role === 'owner' ? <AdminWikiPage /> : <Navigate to="/" />} />
            {/* Data Import Tool - PRD-055 #114 */}
            <Route path="/admin/data-import" element={currentUser?.role === 'admin' || currentUser?.role === 'owner' ? <AdminDataImport /> : <Navigate to="/" />} />
            <Route path="/admin/gear" element={currentUser?.role === 'admin' || currentUser?.role === 'owner' ? <GearManagerPage /> : <Navigate to="/" />} />
            <Route path="/admin/wikelo-contracts" element={currentUser?.role === 'admin' || currentUser?.role === 'owner' ? <WikeloContractsPage /> : <Navigate to="/" />} />
            {/* Admin Field Configuration - Unified Item System */}
            <Route path="/admin/field-config" element={currentUser?.role === 'admin' || currentUser?.role === 'owner' ? <FieldConfigManager /> : <Navigate to="/" />} />
            {/* Event Management - PRD-020 Module 2 */}
            <Route path="/admin/events" element={['moderator', 'admin', 'owner'].includes(currentUser?.role) ? <EventManagement /> : <Navigate to="/" />} />
            {/* Activity Types Management - PRD-020 CrewLink */}
            <Route path="/admin/activity-types" element={['admin', 'owner'].includes(currentUser?.role) ? <ActivityTypesManager /> : <Navigate to="/" />} />
            {/* Ship Matrix Configuration - Manufacturers & Ship Types */}
            <Route path="/admin/ship-matrix-config" element={['moderator', 'admin', 'owner'].includes(currentUser?.role) ? <ShipMatrixConfigManager /> : <Navigate to="/" />} />
            {/* Environment Access Management - Owner Only */}
            <Route path="/admin/environment-access" element={currentUser?.role === 'owner' ? <EnvironmentAccessManagement /> : <Navigate to="/" />} />
            {/* Security Dashboard - PRD-037 */}
            <Route path="/admin/security" element={currentUser?.role === 'admin' || currentUser?.role === 'owner' ? <SecurityDashboard /> : <Navigate to="/" />} />
            {/* Ship Acquisition Management - PRD-047 */}
            <Route path="/admin/ship-acquisition" element={currentUser?.role === 'admin' || currentUser?.role === 'owner' ? <ShipAcquisitionManager /> : <Navigate to="/" />} />
            {/* Ship Health Dashboard - PRD-050 */}
            <Route path="/admin/ship-health" element={['moderator', 'admin', 'owner'].includes(currentUser?.role) ? <ShipHealthDashboard /> : <Navigate to="/" />} />
            {/* PRD-086 Phase 9: Most Wanted Queue */}
            <Route path="/admin/most-wanted" element={['moderator', 'admin', 'owner'].includes(currentUser?.role) ? <MostWantedQueue /> : <Navigate to="/" />} />
            {/* PRD-086 Phase 10: Keyword Moderation */}
            <Route path="/admin/keyword-moderation" element={['moderator', 'admin', 'owner'].includes(currentUser?.role) ? <KeywordModerationPage /> : <Navigate to="/" />} />
            {/* PRD-060: Patch Management */}
            <Route path="/admin/patches" element={<ProtectedAdminRoute><PatchManagement /></ProtectedAdminRoute>} />

            {/* 404 - Not Found */}
            <Route path="*" element={<NotFoundPage />} />
            </>
            )}
            </Routes>
            </Suspense>
            </ErrorBoundary>
          </main>
          {/* PRD-055 #112: CIG Legal Disclaimer Footer */}
          {(!needsAuth || hasAccess) && <Footer />}
          {/* GDPR Cookie Consent - fixed position at bottom, shown on all pages */}
          <CookieConsent />
        </div>
      </ToastProvider>
    </Router>
  );
};

// Root App component (provider is applied at index.js)
const App = () => {
  try { document && document.documentElement && document.documentElement.classList.add('tol-hydrated'); } catch (_) {}
  return (
    <>
      <AppContent />
      <Analytics />
    </>
  );
};

export default App;
